import { Checkbox, Group, Select, Stack, Switch, Text } from '@mantine/core';

import { COUNTRY_OPTIONS } from './orderDraft';
import type { CompanyRow, OrderCompanies } from './useOrderCompanies';

interface EveryCompanySwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** Offered above the company step when the report has several company
 * subjects: one pass for all of them, or today's one-company order. */
export function EveryCompanySwitch({ checked, onChange }: Readonly<EveryCompanySwitchProps>) {
  return (
    <Stack gap={4} mt="sm">
      <Switch
        label="Order for every company on this report"
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        data-testid="registry-order-every-company"
      />
      {checked && (
        <Text size="xs" c="dimmed">
          Each order is placed by name with the subject's details, and Armadillo's desk identifies
          the company. To pick an exact Armadillo match, order for one company at a time.
        </Text>
      )}
    </Stack>
  );
}

/** Step 1 when ordering for every company - which of them, and where each
 * order goes. */
export function CompaniesStep({ companies }: Readonly<{ companies: OrderCompanies }>) {
  return (
    <Stack gap="xs" mt="sm" data-testid="registry-order-companies">
      <Text size="xs" c="dimmed">
        Untick a company to leave it out. Each needs the country whose registry holds its documents.
      </Text>
      {companies.rows.map((row) => (
        <CompanyLine
          key={row.subject.id}
          row={row}
          onTicked={(ticked) => companies.setTicked(row.subject.id, ticked)}
          onCountry={(country) => companies.setCountry(row.subject.id, country)}
        />
      ))}
    </Stack>
  );
}

function CompanyLine({
  row,
  onTicked,
  onCountry,
}: Readonly<{
  row: CompanyRow;
  onTicked: (ticked: boolean) => void;
  onCountry: (country: string | null) => void;
}>) {
  const { subject } = row;
  const needsCountry = row.ticked && row.country === null;
  return (
    <Group
      wrap="nowrap"
      align="flex-start"
      justify="space-between"
      data-testid={`registry-order-company-${subject.id}`}
    >
      <Checkbox
        checked={row.ticked}
        onChange={(event) => onTicked(event.currentTarget.checked)}
        label={
          <Stack gap={0}>
            <Text size="sm">{subject.name}</Text>
            <Text size="xs" c="dimmed">
              {subject.registrationNumber ?? 'No registration number'}
            </Text>
          </Stack>
        }
      />
      <Select
        aria-label={`Country for ${subject.name}`}
        placeholder="Pick a country"
        data={COUNTRY_OPTIONS}
        value={row.country}
        onChange={onCountry}
        searchable
        required={row.ticked}
        disabled={!row.ticked}
        error={needsCountry ? 'Pick a country' : undefined}
        comboboxProps={{ withinPortal: false }}
        w={260}
        style={{ flexShrink: 0 }}
        data-testid={`registry-order-company-country-${subject.id}`}
      />
    </Group>
  );
}
